import { Fragment, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Button, Text } from '@servicetitan/anvil2';

import { ChatLayout } from '../../common/components/chat-layout/chat-layout';
import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';
import { Breadcrumbs } from '../../common/components/breadcrumbs/breadcrumbs';
import { FooterRow } from '../../common/components/footer-row/footer-row';
import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { EquipmentAnswerCiteViewModel, QuestionsStore } from '../stores/questions.store';
import { MessageRow } from '../../common/components/message-row/message-row';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { QuestionsOptions } from './questions-options';
import { IQuestionOptionModel } from '../models/questions.model';
import { MessageType } from '../../common/models/message-type.model';
import { CustomerInfo } from '../../common/components/entity-components/customer-info/customer-info';
import { CustomerInfoModel } from '../../common/models/customer.model';
import { LocationList } from '../../common/components/entity-components/location-list/location-list';
import { LocationDetailModel, LocationListModel } from '../../common/models/location.model';
import { LocationDetail } from '../../common/components/entity-components/location-detail/location-detail';
import { EquipmentList } from '../../common/components/entity-components/equipment-list/equipment-list';
import { EquipmentDetail } from '../../common/components/entity-components/equipment-detail/equipment-detail';
import { EquipmentItemModel, EquipmentListModel } from '../../common/models/equipment.model';
import { JobList } from '../../common/components/entity-components/job-list/job-list';
import { JobItemModel, JobListModel } from '../../common/models/job.model';
import { JobDetail } from '../../common/components/entity-components/job-detail/job-detail';
import { ErrorView } from '../../common/components/error-view';
import { ErrorModel } from '../../common/models/error.model';
import { Loader } from '../../common/components/loader/loader';
import { Feedback } from '../../common/components/feedback/feedback';
import { FeedbackOptions } from '../../common/components/feedback/feedback-options';
import { FeedbackDetails } from '../../common/components/feedback/feedback-details';
import { Markdown } from '../../common/components/entity-components/markdown/markdown';
import { InvoiceList } from '../../common/components/entity-components/invoice-list/invoice-list';
import { InvoiceItemModel, InvoiceListModel } from '../../common/models/invoice.model';
import { InvoiceDetail } from '../../common/components/entity-components/invoice-detail/invoice-detail';
import { EstimateList } from '../../common/components/entity-components/estimate-list/estimate-list';
import { EstimateItemModel, EstimateListModel } from '../../common/models/estimate.model';
import { EstimateDetail } from '../../common/components/entity-components/estimate-detail/estimate-detail';
import { DownloadDocument } from '../../common/components/download-document/download-document';
import { EquipmentDocumentApiModel } from '../../common/api/equipments.api';
import { EquipmentAnswer } from './equipment-answer';
import { BattlecardLoadingMask } from '../../common/components/battlecard-loading-mask';
import { BattlecardStore } from '../../common/stores/battlecard.store';
import { PdfViewer } from '../../common/components/pdf-viewer/pdf-viewer';
import { Takeover } from '@servicetitan/design-system';
import { MainStore } from '../../common/stores/main.store';
import { navigateToClose } from '../../common/utils/navigate-to-close';

export const QuestionsPage = provide({ singletons: [QuestionsStore] })(
    observer(() => {
        const [chatStore, questionsStore, battlecardStore, { showCloseInChatPage, selectedTopic }] =
            useDependencies<[ChatStore, QuestionsStore, BattlecardStore, MainStore]>(
                ChatStore,
                QuestionsStore,
                BattlecardStore,
                MainStore
            );
        const history = useHistory();

        useEffect(() => {
            questionsStore.proceedFrom();
            return () => {
                questionsStore.dispose();
            };
        }, [questionsStore]);

        const handleBack = () => {
            history.goBack();
        };
        const handleClose = () => {
            navigateToClose();
        };

        const handleMessage = useCallback(
            (message: string) => {
                return questionsStore.handleMessage(message);
            },
            [questionsStore]
        );

        const handleQuestionClick = useCallback(
            (question: IQuestionOptionModel) => {
                questionsStore.handleQuestionSelect(question);
            },
            [questionsStore]
        );

        const [pdfUrl, setPdfUrl] = useState<
            { doc: EquipmentDocumentApiModel; page: number } | undefined
        >();

        const renderMessage = (message: IMessage, key: string) => {
            switch (message.type) {
                case MessageType.Button:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            name={message.isMine ? 'Tenant Name' : 'TI'}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <Button onClick={message.onClick}>{message.text}</Button>
                        </MessageRow>
                    );
                case MessageType.Text:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            name={message.isMine ? 'Tenant Name' : 'TI'}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Text>{message.text}</Text>
                            </MessageBubble>
                        </MessageRow>
                    );
                case MessageType.RichText:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            name={message.isMine ? 'Tenant Name' : 'TI'}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Text>
                                    {/* eslint-disable react/no-danger,@typescript-eslint/naming-convention */}
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: message.text ?? '',
                                        }}
                                    />
                                    {/* eslint-enable react/no-danger,@typescript-eslint/naming-convention */}
                                </Text>
                            </MessageBubble>
                        </MessageRow>
                    );
                case MessageType.Question:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <QuestionsOptions
                                key={key}
                                questions={message.data as IQuestionOptionModel[]}
                                onSelect={handleQuestionClick}
                            />
                        </MessageRow>
                    );
                case MessageType.CustomerInfo:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <CustomerInfo data={message.data as CustomerInfoModel} />
                        </MessageRow>
                    );

                case MessageType.LocationList:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <LocationList data={message.data as LocationListModel} />
                        </MessageRow>
                    );

                case MessageType.LocationDetail:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <LocationDetail data={message.data as LocationDetailModel} />
                        </MessageRow>
                    );

                case MessageType.JobList:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <JobList data={message.data as JobListModel} />
                        </MessageRow>
                    );

                case MessageType.JobDetails:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <JobDetail data={message.data as JobItemModel} />
                        </MessageRow>
                    );

                case MessageType.EquipmentList:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <EquipmentList data={message.data as EquipmentListModel} />
                        </MessageRow>
                    );

                case MessageType.EquipmentDetails:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            data-cy={`message-row-${key}`}
                        >
                            <EquipmentDetail data={message.data as EquipmentItemModel} />
                        </MessageRow>
                    );

                case MessageType.Error:
                    return selectedTopic?.id === 1 ? (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <ErrorView data={message.data as ErrorModel} />
                        </MessageRow>
                    ) : null;

                case MessageType.Feedback:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <Feedback />
                        </MessageRow>
                    );

                case MessageType.FeedbackOptions:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <FeedbackOptions />
                        </MessageRow>
                    );

                case MessageType.FeedbackDetails:
                    return (
                        <MessageRow
                            showAvatar={message.showAvatar}
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <FeedbackDetails />
                        </MessageRow>
                    );

                case MessageType.Markdown:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Markdown text={message.data as string} />
                            </MessageBubble>
                        </MessageRow>
                    );

                case MessageType.InvoiceList:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <InvoiceList data={message.data as InvoiceListModel} />
                        </MessageRow>
                    );

                case MessageType.InvoiceDetails:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <InvoiceDetail data={message.data as InvoiceItemModel} />
                        </MessageRow>
                    );

                case MessageType.EstimateList:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <EstimateList data={message.data as EstimateListModel} />
                        </MessageRow>
                    );

                case MessageType.EstimateDetails:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <EstimateDetail data={message.data as EstimateItemModel} />
                        </MessageRow>
                    );

                case MessageType.DownloadDocuments: {
                    const data = (message.data as EquipmentDocumentApiModel[]).map(x => ({
                        doc: x,
                        page: 0,
                    }));
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <DownloadDocument
                                documents={data}
                                onViewPdf={(document: EquipmentDocumentApiModel, page: number) => {
                                    setPdfUrl({ doc: document, page });
                                }}
                            />
                        </MessageRow>
                    );
                }

                case MessageType.EquipmentAnswerCite:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <EquipmentAnswer
                                answer={message.data as EquipmentAnswerCiteViewModel}
                                onViewPdf={(document: EquipmentDocumentApiModel, page: number) => {
                                    setPdfUrl({ doc: document, page });
                                }}
                            />
                        </MessageRow>
                    );

                default:
                    return null;
            }
        };

        return (
            <Fragment>
                {pdfUrl?.doc?.url && pdfUrl.doc.download_file_name && (
                    <Takeover
                        theme="light"
                        title={pdfUrl.doc.download_file_name}
                        onClose={() => setPdfUrl(undefined)}
                    >
                        <Takeover.Section className="p-0-i">
                            <PdfViewer
                                file={`/api/v1/document/proxy-pdf?url=${encodeURIComponent(pdfUrl.doc.url)}`}
                                pageNumber={pdfUrl.page}
                                fileName={pdfUrl.doc.download_file_name}
                            />
                        </Takeover.Section>
                    </Takeover>
                )}
                <ChatLayout
                    header={
                        <ChatHeaderNew
                            icon={showCloseInChatPage ? 'close' : 'back'}
                            onNavigationButtonClick={showCloseInChatPage ? handleClose : handleBack}
                        />
                    }
                    subHeader={<Breadcrumbs />}
                    footer={
                        <FooterRow
                            onMessageSend={handleMessage}
                            isLoading={questionsStore.loading}
                            disabled={
                                questionsStore.isStInfoTopicSelected &&
                                battlecardStore.battlecardError === true
                            }
                        />
                    }
                >
                    <BattlecardLoadingMask active={questionsStore.showBattlecardLoading}>
                        {chatStore.messages.map((message, index) => {
                            return renderMessage(message, index.toString());
                        })}
                        {questionsStore.loading && (
                            <MessageRow isMine={false}>
                                <Loader />
                            </MessageRow>
                        )}
                    </BattlecardLoadingMask>
                </ChatLayout>
            </Fragment>
        );
    })
);
